import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import type { ScanTestResponse } from '../../types/strategyLab';
import { SCANNER_FIELD_MAP } from './scannerFields';
import { fmtNum, fmtUsd, fmtPct, pnlColorClass } from './format';

interface ScanPreviewTableProps {
  result: ScanTestResponse;
}

/** Renders scan-test matches: symbol, price, change%, and the matched field values. */
const ScanPreviewTable: React.FC<ScanPreviewTableProps> = ({ result }) => {
  // Union of all value keys across matches → columns (stable order by first appearance).
  const valueKeys: string[] = [];
  for (const m of result.matches) {
    for (const k of Object.keys(m.values || {})) {
      if (!valueKeys.includes(k)) valueKeys.push(k);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge variant="success">{result.match_count} matches</Badge>
        <span className="text-muted-foreground">
          scanned {result.total_scanned.toLocaleString()} symbols in {result.scan_duration_ms} ms
        </span>
      </div>

      {result.matches.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          No symbols matched. Loosen the conditions or widen the universe.
        </p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Symbol</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Change %</TableHead>
                {valueKeys.map((k) => (
                  <TableHead key={k} className="text-right whitespace-nowrap">
                    {SCANNER_FIELD_MAP[k]?.label ?? k}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.matches.map((m) => (
                <TableRow key={m.symbol}>
                  <TableCell className="font-bold">{m.symbol}</TableCell>
                  <TableCell className="text-right">{fmtUsd(m.price, { cents: true })}</TableCell>
                  <TableCell className={`text-right font-medium ${pnlColorClass(m.change_percent)}`}>
                    {fmtPct(m.change_percent, 2)}
                  </TableCell>
                  {valueKeys.map((k) => (
                    <TableCell key={k} className="text-right text-muted-foreground whitespace-nowrap">
                      {fmtNum(m.values?.[k] as number | string | null | undefined)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ScanPreviewTable;
